import React, { useState } from "react";
import { STATUS_META } from "./connectionMap";

/**
 * ComponentEntry — the ONE mould for documenting any component in the styleguide.
 *
 * Every future component is added by filling this same shape, so the guide stays
 * uniform and a new component can be built by copying an existing entry. Renders,
 * in a fixed order:
 *
 *   1. Header  — name · tier chip · status tag · connection count
 *   2. Blurb   — one line on what it is / when to use it
 *   3. Demo    — the REAL component, live (children)
 *   4. Snippet — copy-paste JSX, with a Copy button
 *   5. Props   — a table of prop / values / default
 *   6. Guidance— optional do / don't bullets
 *
 * Props:
 *   name      string                  — display name (e.g. "Button", "Product Card")
 *   tier      string?                 — visual tier id (cta-hero, cta-primary, …)
 *   status    keyof STATUS_META?      — shared | standalone | internal | orphaned | legacy
 *   importFrom string?                — the import path, shown as a code line
 *   count, sites  number, string[]?   — connection map (uses the shared <Connections/>)
 *   blurb     node?
 *   snippet   string?                 — JSX to show + copy
 *   props     [{ prop, values, default, note? }]?
 *   guidance  { do?: string[], dont?: string[] }?
 *   children  node                    — the live demo
 */

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = () => {
    // Clipboard API where available; the styleguide is dev-only so a silent
    // failure (older context) is acceptable — the snippet is still selectable.
    try {
      navigator.clipboard?.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      /* no-op */
    }
  };
  return (
    <button type="button" className="sg-copy" onClick={onCopy} aria-label="Copy code">
      {copied ? "✓ Copied" : "Copy"}
    </button>
  );
};

const TierChip = ({ tier }) =>
  tier ? <span className="sg-tier" title="Visual tier">{tier}</span> : null;

const StatusTag = ({ status }) => {
  if (!status) return null;
  const meta = STATUS_META[status] || STATUS_META.unused;
  return (
    <span className={`sg-tag sg-tag--${meta.tone}`} title={meta.hint}>
      {meta.label}
    </span>
  );
};

const Connections = ({ count = 0, sites = [] }) => {
  const [open, setOpen] = useState(false);
  if (!count) return <span className="sg-conn sg-conn--none">0 connections</span>;
  return (
    <div className="sg-conn">
      <button type="button" className="sg-conn__toggle" onClick={() => setOpen((v) => !v)}>
        {open ? "▾" : "▸"} {count} connection{count !== 1 ? "s" : ""}
      </button>
      {open && (
        <ul className="sg-conn__list">
          {sites.map((f) => <li key={f}><code>{f}</code></li>)}
        </ul>
      )}
    </div>
  );
};

const ComponentEntry = ({
  name,
  importName,
  tier,
  status,
  importFrom,
  count,
  sites,
  blurb,
  snippet,
  props = [],
  guidance,
  children,
}) => (
  <article className="sg-entry sg-row">
    <header className="sg-entry__head sg-row__head">
      <code className="sg-row__name">{name}</code>
      <TierChip tier={tier} />
      <StatusTag status={status} />
      <Connections count={count ?? (sites ? sites.length : 0)} sites={sites} />
    </header>

    {importFrom && (
      <p className="sg-entry__import"><code>import {`{ ${importName || name.split(" ")[0]} }`} from "{importFrom}"</code></p>
    )}
    {blurb && <p className="sg-row__note sg-entry__blurb">{blurb}</p>}

    {/* 3 — live demo */}
    <div className="sg-entry__demo sg-specimens">{children}</div>

    {/* 4 — copy-paste snippet */}
    {snippet && (
      <div className="sg-entry__snippet">
        <CopyButton text={snippet} />
        <pre><code>{snippet}</code></pre>
      </div>
    )}

    {/* 5 — props table */}
    {props.length > 0 && (
      <table className="sg-entry__props sg-table">
        <thead>
          <tr><th>Prop</th><th>Values</th><th>Default</th></tr>
        </thead>
        <tbody>
          {props.map((p) => (
            <tr key={p.prop}>
              <td><code>{p.prop}</code></td>
              <td>{p.values}{p.note && <span className="sg-entry__propnote"> — {p.note}</span>}</td>
              <td><code>{p.default ?? "—"}</code></td>
            </tr>
          ))}
        </tbody>
      </table>
    )}

    {/* 6 — do / don't */}
    {guidance && (guidance.do || guidance.dont) && (
      <div className="sg-entry__guide">
        {guidance.do && (
          <ul className="sg-guide sg-guide--do">
            {guidance.do.map((g) => <li key={g}>{g}</li>)}
          </ul>
        )}
        {guidance.dont && (
          <ul className="sg-guide sg-guide--dont">
            {guidance.dont.map((g) => <li key={g}>{g}</li>)}
          </ul>
        )}
      </div>
    )}
  </article>
);

export default ComponentEntry;
